/**
 * Scout — panel opiekuna APIS (/scout/opiekun).
 * Obsługa zgłoszeń klientów: lista, statusy, odpowiedź, podgląd profilu firmy,
 * odesłanie odpowiedzi do Skrzynki Scouta klienta.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import {
  LifeBuoy, Loader2, Check, Send, Building2, ChevronDown, ChevronUp, Inbox,
} from 'lucide-react';

const panel = 'rounded-2xl border border-slate-700 bg-slate-800/60';
const chip = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold';

const STATUSES = [
  { id: 'nowe', label: 'Nowe', color: '#F59E0B' },
  { id: 'w_trakcie', label: 'W trakcie', color: '#3B82F6' },
  { id: 'odpowiedziane', label: 'Odpowiedziane', color: '#10B981' },
  { id: 'zamkniete', label: 'Zamknięte', color: '#9CA3AF' },
];

export default function ScoutOpiekun() {
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState('wszystkie');

  const load = () => {
    fetch(`${API}/scout/admin/tickets`)
      .then((r) => r.json())
      .then((d) => setTickets(d?.ok ? d.tickets : []))
      .catch(() => setTickets([]));
  };
  useEffect(() => { load(); }, []);

  const patch = (id, updates) => setTickets((list) => list.map((t) => (t.id === id ? { ...t, ...updates } : t)));

  const visible = (tickets || []).filter((t) => filter === 'wszystkie' || (t.status || 'nowe') === filter);

  return (
    <div className="min-h-screen bg-slate-900 text-white p-5 md:p-10" style={{ fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="scout-opiekun">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2.5"><LifeBuoy className="w-6 h-6 text-[#FF8A3D]" /> Panel opiekuna APIS</h1>
          <p className="text-sm text-slate-400 mt-1">Zgłoszenia klientów Scouta — pytania, które wykraczają poza możliwości asystenta.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <FilterBtn on={filter === 'wszystkie'} onClick={() => setFilter('wszystkie')} testid="tickets-filter-all">
            Wszystkie ({(tickets || []).length})
          </FilterBtn>
          {STATUSES.map((s) => (
            <FilterBtn key={s.id} on={filter === s.id} onClick={() => setFilter(s.id)} testid={`tickets-filter-${s.id}`}>
              {s.label} ({(tickets || []).filter((t) => (t.status || 'nowe') === s.id).length})
            </FilterBtn>
          ))}
        </div>

        {tickets === null && <div className={`${panel} p-10 text-center`}><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#FF8A3D]" /></div>}

        {tickets !== null && visible.length === 0 && (
          <div className={`${panel} p-10 text-center text-sm text-slate-400`} data-testid="tickets-empty">
            Brak zgłoszeń{filter !== 'wszystkie' ? ' w tym statusie' : ''}.
          </div>
        )}

        <div className="space-y-4">
          {visible.map((t) => <TicketCard key={t.id} t={t} onPatch={patch} />)}
        </div>
      </div>
    </div>
  );
}

function FilterBtn({ children, on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-xl px-3.5 py-2 text-xs font-semibold border transition ${on ? 'bg-[#F26A21] border-transparent text-white' : 'border-slate-600 text-slate-400 hover:bg-slate-800'}`}>
      {children}
    </button>
  );
}

function TicketCard({ t, onPatch }) {
  const st = STATUSES.find((s) => s.id === (t.status || 'nowe')) || STATUSES[0];
  const [answer, setAnswer] = useState(t.answer || '');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState(null);

  const update = async (updates) => {
    const res = await fetch(`${API}/scout/admin/tickets/${t.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
    });
    const d = await res.json();
    if (d?.ok) onPatch(t.id, d.ticket);
    return d;
  };

  const saveAnswer = async () => {
    if (saving) return;
    setSaving(true); setMsg('');
    const d = await update({ answer });
    setMsg(d?.ok ? 'Odpowiedź zapisana.' : (d?.message || 'Błąd zapisu.'));
    setSaving(false);
  };

  const sendToInbox = async () => {
    if (sending) return;
    setSending(true); setMsg('');
    try {
      const res = await fetch(`${API}/scout/admin/tickets/${t.id}/send-to-inbox`, { method: 'POST' });
      const d = await res.json();
      if (d?.ok) { onPatch(t.id, { status: 'odpowiedziane', sent_to_inbox: true }); setMsg(d.message); }
      else setMsg(d?.message || 'Błąd wysyłki.');
    } catch { setMsg('Błąd połączenia.'); }
    finally { setSending(false); }
  };

  const toggleProfile = async () => {
    setShowProfile(!showProfile);
    if (!profile && !showProfile) {
      try {
        const res = await fetch(`${API}/scout/clients/${t.client_id}`);
        const d = await res.json();
        if (d?.ok) setProfile(d.client);
      } catch { /* noop */ }
    }
  };

  return (
    <div className={`${panel} p-5`} data-testid={`ticket-card-${t.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-bold">{t.client_name || t.client_id}</div>
          <div className="text-[11px] text-slate-500">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</div>
        </div>
        <select value={t.status || 'nowe'} onChange={(e) => update({ status: e.target.value })} data-testid={`ticket-status-${t.id}`}
          className="rounded-lg border border-slate-600 bg-slate-800 px-2.5 py-1.5 text-xs font-semibold cursor-pointer focus:outline-none" style={{ color: st.color }}>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {t.sent_to_inbox && <span className={`${chip} bg-emerald-500/15 text-emerald-300 border border-emerald-400/30`}><Inbox className="w-3 h-3" /> W Skrzynce klienta</span>}
      </div>

      <p className="mt-3 text-sm text-slate-200 leading-relaxed rounded-xl bg-slate-900/60 border border-slate-700 p-3.5" data-testid={`ticket-question-${t.id}`}>{t.question}</p>

      <button onClick={toggleProfile} data-testid={`ticket-profile-toggle-${t.id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-orange-300 transition">
        <Building2 className="w-3.5 h-3.5" /> Profil firmy {showProfile ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {showProfile && (
        <div className="mt-2 rounded-xl bg-slate-900/60 border border-slate-700 p-3.5 text-xs text-slate-300 space-y-1" data-testid={`ticket-profile-${t.id}`}>
          {profile ? (
            <>
              <div><span className="text-slate-500">Branża:</span> {profile.industry || '—'} {profile.city ? `· ${profile.city}` : ''}</div>
              <div><span className="text-slate-500">Oferta:</span> {profile.offer || '—'}</div>
              <div><span className="text-slate-500">Cel:</span> {profile.goal_30d || '—'}</div>
              <div><span className="text-slate-500">Ton:</span> {profile.tone || '—'} <span className="text-slate-500 ml-2">Unikać:</span> {profile.avoid || '—'}</div>
              <div><span className="text-slate-500">Profil uzupełniony:</span> {profile.completeness}%</div>
            </>
          ) : <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
      )}

      <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid={`ticket-answer-${t.id}`}
        placeholder="Odpowiedź opiekuna dla klienta…"
        className="mt-3 w-full min-h-[90px] rounded-xl bg-slate-900/60 border border-slate-700 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50" />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={saveAnswer} disabled={saving || !answer.trim()} data-testid={`ticket-save-${t.id}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3.5 py-2 text-sm font-semibold text-white transition disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Zapisz odpowiedź
        </button>
        <button onClick={sendToInbox} disabled={sending || !(t.answer || '').trim()} data-testid={`ticket-send-inbox-${t.id}`}
          title={!(t.answer || '').trim() ? 'Najpierw zapisz odpowiedź' : ''}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3.5 py-2 text-sm font-semibold text-slate-300 hover:bg-slate-800 transition disabled:opacity-40">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Wyślij do Skrzynki klienta
        </button>
        {msg && <span className="text-xs text-emerald-400" data-testid={`ticket-msg-${t.id}`}>{msg}</span>}
      </div>
    </div>
  );
}
